import type { FilterObjectForSemanticSearch, VehicleObjectForSimilaritySearch } from '@makemydeal/dr-platform-types';
import type { AppliedFilter, CatalogVehicle, FilterSummary } from '../types';
import type { CommonOrgDealerVehicle } from '@makemydeal/dr-shared-types/vehicleTypes';
import { InventoryTypeFilters } from '../components/buildVehicle/types/BuildVehicleSelects';
import { catalogToVehicle } from './catalogToVehicle';
import { isVinLike } from './aiInventoryFilter';
import { AI_PROMPT_KIND, AI_PROMPT_MODE, AI_PROMPT_PLACEHOLDER } from '../constants';

export interface ProcessedAgentResponse {
    agentVehicleResponse: CatalogVehicle[];
    showAgentProcessingError: boolean;
}

export const getFilteredAISearchResults = (vin?: string, stockNumber?: string, vehicles?: CatalogVehicle[]) => {
    if ((!vin && !stockNumber) || !vehicles) {
        return { filteredAISearchResults: vehicles };
    }

    // Match on either identifier alone — a vin-and-AND-stockNumber match silently no-ops whenever a
    // candidate legitimately has only one of the two (e.g. an AI result returned without a stockNumber, or a
    // re-keyed vehicle that kept its VIN). Same predicate as useAiAltVehicleOptions.ts's isPrimaryVehicle.
    // Deliberately NOT mirrored in getFilteredSearchResults (filterSearchResults.ts) — that file's classic
    // search spans cross-dealership rooftops where stock numbers collide, so it stays AND. Don't unify them.
    const filteredAISearchResults = vehicles.filter(
        (vehicle) => !((!!vin && vehicle.vin === vin) || (!!stockNumber && vehicle.stockNumber === stockNumber))
    );

    return { filteredAISearchResults };
};

export function processAgentVehicleResponse(
    agentResponseData: CommonOrgDealerVehicle[],
    vehicleVin?: string,
    vehicleStockNumber?: string
): ProcessedAgentResponse {
    const hasVehicles = agentResponseData && agentResponseData.length > 0;

    if (!hasVehicles) {
        return {
            agentVehicleResponse: [],
            showAgentProcessingError: true
        };
    }

    const mappedVehicles = agentResponseData.map(catalogToVehicle);
    const { filteredAISearchResults } = getFilteredAISearchResults(vehicleVin, vehicleStockNumber, mappedVehicles);
    const agentSearchTotalCount = filteredAISearchResults?.length || 0;

    return {
        agentVehicleResponse: filteredAISearchResults || [],
        showAgentProcessingError: agentSearchTotalCount === 0
    };
}

export function normalizeVehicleCondition(condition?: string): 'NEW' | 'USED' | undefined {
    if (!condition) return undefined;

    const vehicleCondition = condition.toUpperCase().replace(/[^A-Z]/g, '');

    if (vehicleCondition === 'NEW' || vehicleCondition.includes('NEW')) {
        return InventoryTypeFilters.NEW;
    } else if (
        vehicleCondition === 'USED' ||
        vehicleCondition.includes('USED') ||
        vehicleCondition === 'CERTIFIED' ||
        vehicleCondition.includes('CERTIFIED') ||
        vehicleCondition === 'CPO'
    ) {
        return InventoryTypeFilters.USED;
    }

    return undefined;
}

/** Shared by `getCurrentVehicleForSimilaritySearch` and any host seeding the search from another vehicle. */
export function buildVehicleForSimilaritySearch(vehicle: any): VehicleObjectForSimilaritySearch | undefined {
    const condition = normalizeVehicleCondition(vehicle?.condition);

    if (!vehicle || !vehicle.year || !vehicle.make || !vehicle.model || !condition) {
        return undefined;
    }

    const vehicleObject: VehicleObjectForSimilaritySearch = {
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        condition
    };

    // Add optional properties only if they have values
    if (vehicle.vin) vehicleObject.vin = vehicle.vin;
    if (vehicle.trim) vehicleObject.trim = vehicle.trim;
    if (vehicle.bodyStyle) vehicleObject.bodyStyle = vehicle.bodyStyle;
    if (vehicle.exteriorColor) vehicleObject.exteriorColor = vehicle.exteriorColor;
    if (vehicle.interiorColor) vehicleObject.interiorColor = vehicle.interiorColor;
    if (vehicle.stockNumber) vehicleObject.stockNumber = vehicle.stockNumber;
    if (vehicle.driveLine) vehicleObject.driveLine = vehicle.driveLine;
    if (vehicle.fuel) vehicleObject.fuel = vehicle.fuel;
    if (vehicle.engine) vehicleObject.engine = vehicle.engine;
    if (vehicle.transmission) vehicleObject.transmission = vehicle.transmission;

    const listedPrice = vehicle.listedPrice || vehicle.retailPrice;
    if (listedPrice) vehicleObject.listedPrice = listedPrice;

    const odometer =
        typeof vehicle.mileage === 'number' ? vehicle.mileage : typeof vehicle.odometer === 'number' ? vehicle.odometer : undefined;
    if (odometer) vehicleObject.odometer = odometer;

    return vehicleObject;
}

/** Only YMMT, which is all the similar-vehicle search identifies a base vehicle by. */
export function buildVehicleIdentifier(vehicle: any): string | undefined {
    if (vehicle && vehicle.year && vehicle.make && vehicle.model) {
        const { year, make, model, trim } = vehicle;
        return `${year} ${make} ${model}` + (trim ? ` ${trim}` : ``);
    }
    return undefined;
}

export const getMatchingFilterValues = (
    appliedFilters: AppliedFilter[],
    filterId: string,
    agentFilterSummary?: FilterSummary
): string[] => {
    const summaryKey = filterId as keyof Omit<FilterSummary, 'count'>;

    return appliedFilters
        .filter((filter) => filter.id === filterId)
        .flatMap((filter) => {
            // Find matching key in agentFilterSummary using case-insensitive comparison
            const summaryOptions = agentFilterSummary?.[summaryKey];
            const matchingKey = summaryOptions
                ? Object.keys(summaryOptions).find((key) => key.toLowerCase() === filter.label.toLowerCase())
                : undefined;
            // If no match found in summary, fall back to using the filter label directly
            // This allows selecting filters that may not be in the current contextual summary
            // (e.g., selecting CERTIFIED after USED has already filtered the summary)
            return matchingKey ? [matchingKey] : [filter.label];
        });
};

export const formatFilterObject = (
    appliedFilters?: AppliedFilter[],
    agentFilterSummary?: FilterSummary
): FilterObjectForSemanticSearch => {
    if (!appliedFilters || appliedFilters.length === 0) {
        return {};
    }

    const years = appliedFilters.filter((filter) => filter.id === 'years').map((filter) => Number(filter.label));

    // Get matching filter values for each filter type
    // When USED is selected, also include CERTIFIED to ensure certified vehicles are returned
    const rawConditions = getMatchingFilterValues(appliedFilters, 'inventoryTypes', agentFilterSummary);
    const hasUsed = rawConditions.some((c) => c.toUpperCase() === InventoryTypeFilters.USED);
    const hasCertified = rawConditions.some((c) => c.toUpperCase() === InventoryTypeFilters.CERTIFIED);
    const conditions = hasUsed && !hasCertified ? [...rawConditions, InventoryTypeFilters.CERTIFIED] : rawConditions;
    const makes = getMatchingFilterValues(appliedFilters, 'makes', agentFilterSummary);
    const models = getMatchingFilterValues(appliedFilters, 'models', agentFilterSummary);
    const trims = getMatchingFilterValues(appliedFilters, 'trims', agentFilterSummary);
    const bodyStyles = getMatchingFilterValues(appliedFilters, 'bodyStyles', agentFilterSummary);
    const engines = getMatchingFilterValues(appliedFilters, 'engines', agentFilterSummary);
    const exteriorColors = getMatchingFilterValues(appliedFilters, 'extColors', agentFilterSummary);
    const interiorColors = getMatchingFilterValues(appliedFilters, 'intColors', agentFilterSummary);

    // Get min/max values for numeric filters
    const minPriceFilter = appliedFilters.find((filter) => filter.queryParameter === 'minPrice');
    const maxPriceFilter = appliedFilters.find((filter) => filter.queryParameter === 'maxPrice');
    const minOdometerFilter = appliedFilters.find((filter) => filter.queryParameter === 'minOdometer');
    const maxOdometerFilter = appliedFilters.find((filter) => filter.queryParameter === 'maxOdometer');
    const minAgeDaysFilter = appliedFilters.find((filter) => filter.queryParameter === 'minAgeDays');
    const maxAgeDaysFilter = appliedFilters.find((filter) => filter.queryParameter === 'maxAgeDays');

    const filterObject: FilterObjectForSemanticSearch = {};

    if (conditions.length > 0) filterObject.condition = conditions;
    if (makes.length > 0) filterObject.make = makes;
    if (models.length > 0) filterObject.model = models;
    if (trims.length > 0) filterObject.trim = trims;
    if (bodyStyles.length > 0) filterObject.bodyStyle = bodyStyles;
    if (engines.length > 0) filterObject.engine = engines;
    if (exteriorColors.length > 0) filterObject.exteriorColor = exteriorColors;
    if (interiorColors.length > 0) filterObject.interiorColor = interiorColors;
    // Send both years[] (new format) and minYear/maxYear (legacy) for backwards compatibility
    // dr-agents will prioritize years[] when available, but fall back to minYear/maxYear
    if (years.length > 0) {
        filterObject.years = years;
        // Also send minYear/maxYear for backwards compatibility with deployed dr-agents
        filterObject.minYear = Math.min(...years);
        filterObject.maxYear = Math.max(...years);
    }
    if (minPriceFilter) filterObject.minPrice = Number(minPriceFilter.value);
    if (maxPriceFilter) filterObject.maxPrice = Number(maxPriceFilter.value);
    if (minOdometerFilter) filterObject.minMileage = Number(minOdometerFilter.value);
    if (maxOdometerFilter) filterObject.maxMileage = Number(maxOdometerFilter.value);
    if (minAgeDaysFilter) filterObject.minAgeDays = Number(minAgeDaysFilter.value);
    if (maxAgeDaysFilter) filterObject.maxAgeDays = Number(maxAgeDaysFilter.value);

    return filterObject;
};

/**
 * Normalizes filter option objects by converting all keys to uppercase and merging duplicate counts.
 * This handles API responses that return mixed case values (e.g., "Ford" and "FORD", "Black" and "BLACK").
 * Returns options sorted alphabetically by key.
 */
export const normalizeFilterOptions = (options?: Record<string, number>) => {
    if (!options) return options;

    const normalized = Object.entries(options).reduce((acc, [option, count]) => {
        const key = option.toUpperCase();
        acc[key] = (acc[key] || 0) + count;
        return acc;
    }, {} as Record<string, number>);

    return Object.fromEntries(Object.entries(normalized).sort(([a], [b]) => a.localeCompare(b)));
};

/**
 * Normalizes inventory type options by merging CERTIFIED counts into USED.
 * CPO (Certified Pre-Owned) vehicles are semantically used vehicles and should
 * not appear as a separate filter option in the UI.
 */
export const normalizeInventoryTypes = (inventoryTypes?: Record<string, number>) => {
    const normalized = normalizeFilterOptions(inventoryTypes);
    if (!normalized) return normalized;

    // Merge CERTIFIED into USED - CPO vehicles are a subset of used vehicles
    if (normalized['CERTIFIED']) {
        normalized['USED'] = (normalized['USED'] || 0) + normalized['CERTIFIED'];
        delete normalized['CERTIFIED'];
    }

    return normalized;
};

/**
 * Normalizes all filter summary options.
 * - String-based filters: uppercase and alphabetically sorted
 * - Years: kept as-is (API returns them in descending order)
 * - Inventory types: CERTIFIED merged into USED
 */
export const normalizeFilterSummary = (summary?: FilterSummary): FilterSummary | undefined => {
    if (!summary) return summary;

    return {
        ...summary,
        inventoryTypes: normalizeInventoryTypes(summary.inventoryTypes) ?? summary.inventoryTypes,
        makes: normalizeFilterOptions(summary.makes) ?? summary.makes,
        models: normalizeFilterOptions(summary.models) ?? summary.models,
        trims: normalizeFilterOptions(summary.trims) ?? summary.trims,
        bodyStyles: normalizeFilterOptions(summary.bodyStyles) ?? summary.bodyStyles,
        engines: normalizeFilterOptions(summary.engines) ?? summary.engines,
        extColors: normalizeFilterOptions(summary.extColors) ?? summary.extColors,
        intColors: normalizeFilterOptions(summary.intColors) ?? summary.intColors
    };
};

export const getPromptKind = (promptString: string, promptMode: string): AI_PROMPT_KIND => {
    if (promptMode === AI_PROMPT_MODE.SIMILARITY_SEARCH) {
        return AI_PROMPT_KIND.SIMILARITY;
    }
    if (promptString === AI_PROMPT_PLACEHOLDER.ALL_VEHICLES) {
        return AI_PROMPT_KIND.ALL_VEHICLES;
    }
    if (promptString === AI_PROMPT_PLACEHOLDER.SEARCH) {
        return AI_PROMPT_KIND.PLACEHOLDER;
    }
    return isVinLike(promptString) ? AI_PROMPT_KIND.VIN_LIKE : AI_PROMPT_KIND.FREE_TEXT;
};
